// AiDogChatBot.js - Animated Dog Mascot & Intelligent AI Chat Assistant
// Displays an animated dog waving hand with "Ask ME" floating on the top-right corner.
// Understands all queries regarding Ownezzy (pricing, free trial, 0% commission, UPI, domains, niches).
// Escalates unrecognized or human support queries to WhatsApp (8893725078) and Email (contact@ownezzy.com).

import React, { useState, useEffect, useRef } from 'react';
import { openCustomWebsiteModal } from '../utils/customModal';
import useDraggable from '../utils/useDraggable';
import './AiDogChatBot.css';

const WHATSAPP_NUMBER = '8893725078';
const WHATSAPP_INTL = '918893725078';
const SUPPORT_EMAIL = 'contact@ownezzy.com';

export const DogMascotSvg = () => (
  <svg viewBox="0 0 100 100" className="dog-svg-mascot" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    {/* Wagging Tail */}
    <g className="dog-tail-wagging">
      <path d="M 24,70 C 14,60 8,42 16,36 C 22,32 26,44 26,64 Z" fill="#d97706" />
      <path d="M 16,36 C 18,34 22,35 24,40 C 20,44 16,42 16,36 Z" fill="#fffbeb" />
    </g>

    {/* Puppy Body */}
    <path d="M 32,58 C 30,72 26,88 28,94 C 29,97 71,97 72,94 C 74,88 70,72 68,58 Z" fill="#f59e0b" />
    
    {/* Fluffy White Chest */}
    <path d="M 41,60 C 37,70 38,88 50,94 C 62,88 63,70 59,60 Z" fill="#fffbeb" />
    
    {/* Red Collar */}
    <path d="M 33,56 Q 50,65 67,56 L 68,61 Q 50,70 32,61 Z" fill="#ef4444" />
    {/* Golden Collar Tag */}
    <circle cx="50" cy="66" r="3.6" fill="#fbbf24" stroke="#d97706" strokeWidth="0.8" />
    <circle cx="50" cy="66" r="1.2" fill="#b45309" />

    {/* Left Paw (resting on belly) */}
    <ellipse cx="36" cy="80" rx="6" ry="4.5" fill="#fbbf24" />
    <circle cx="36" cy="81" r="2.2" fill="#f43f5e" opacity="0.85" />

    {/* Head Group (bobs gently) */}
    <g className="dog-head-group">
      {/* Left Ear */}
      <g className="dog-ear-left">
        <path d="M 28,26 C 15,22 10,40 16,52 C 20,60 28,54 30,40 Z" fill="#d97706" />
        <path d="M 22,32 C 16,35 15,48 20,50 C 24,52 26,44 24,36 Z" fill="#fca5a5" />
      </g>
      
      {/* Right Ear */}
      <g className="dog-ear-right">
        <path d="M 72,26 C 85,22 90,40 84,52 C 80,60 72,54 70,40 Z" fill="#d97706" />
        <path d="M 78,32 C 84,35 85,48 80,50 C 76,52 74,44 76,36 Z" fill="#fca5a5" />
      </g>

      {/* Main Head Circle */}
      <ellipse cx="50" cy="36" rx="24" ry="22" fill="#fbbf24" />

      {/* Sweet Eyebrows */}
      <ellipse cx="40" cy="24" rx="2.5" ry="1.2" fill="#d97706" />
      <ellipse cx="60" cy="24" rx="2.5" ry="1.2" fill="#d97706" />

      {/* Big Sparkling Anime Eyes */}
      <ellipse cx="40" cy="32" rx="4" ry="4.5" fill="#1e293b" />
      <circle cx="38.5" cy="30.5" r="1.6" fill="#ffffff" />
      <circle cx="41.5" cy="33.5" r="0.8" fill="#ffffff" />

      <ellipse cx="60" cy="32" rx="4" ry="4.5" fill="#1e293b" />
      <circle cx="58.5" cy="30.5" r="1.6" fill="#ffffff" />
      <circle cx="61.5" cy="33.5" r="0.8" fill="#ffffff" />

      {/* Cute White Muzzle */}
      <ellipse cx="50" cy="42" rx="12" ry="8" fill="#fffbeb" />

      {/* Shiny Black Nose */}
      <path d="M 46.5,38 C 47,37 53,37 53.5,38 C 54,40 51.5,42.5 50,42.5 C 48.5,42.5 46,40 46.5,38 Z" fill="#1e293b" />
      <ellipse cx="48.5" cy="38.5" rx="1" ry="0.6" fill="#ffffff" />

      {/* Smiling Mouth & Pink Tongue */}
      <path d="M 46,43 Q 50,46 54,43" stroke="#1e293b" strokeWidth="1.2" strokeLinecap="round" fill="none" />
      <path d="M 48.5,44 Q 50,48.5 51.5,44 Z" fill="#f43f5e" />

      {/* Cute Blush Cheeks */}
      <ellipse cx="33" cy="40" rx="3" ry="1.8" fill="#fca5a5" opacity="0.65" />
      <ellipse cx="67" cy="40" rx="3" ry="1.8" fill="#fca5a5" opacity="0.65" />
    </g>

    {/* The Waving Paw / Hand! */}
    <g className="dog-paw-waving">
      {/* Arm connecting to shoulder */}
      <path d="M 64,68 C 65,58 74,48 76,40 C 78,35 84,36 84,42 C 84,52 72,68 66,72 Z" fill="#f59e0b" />
      {/* Raised Round Paw with Paw Pads */}
      <ellipse cx="78" cy="36" rx="6.5" ry="5.5" fill="#fbbf24" stroke="#d97706" strokeWidth="0.8" />
      {/* Big Center Pad */}
      <ellipse cx="78" cy="37.5" rx="3.2" ry="2.4" fill="#f43f5e" />
      {/* 3 Little Toe Beans */}
      <circle cx="74" cy="33.5" r="1.1" fill="#f43f5e" />
      <circle cx="78" cy="32" r="1.2" fill="#f43f5e" />
      <circle cx="82" cy="33.5" r="1.1" fill="#f43f5e" />
    </g>
  </svg>
);

// AI Conversation Knowledge & Intent Handler
function processAiQuery(inputQuery) {
  const q = String(inputQuery || '').trim().toLowerCase();

  // 1. GREETING
  if (/^(hi|hello|hey|namaste|vanakkam|namaskara|halo|good morning|good evening|yo)\b/.test(q)) {
    return {
      text: "Woof! Hello there! 👋 I'm your Ownezzy AI Buddy.\n\nI can help you with anything about launching your online store in 60 seconds with a 14-day free trial, 0% commission, and direct UPI payments.\n\nWhat would you like to know?",
      actions: [
        { label: "👀 View Demo Stores", type: "demos" },
        { label: "✨ Create Custom Website", type: "custom-modal" },
        { label: "🚀 Start 14-Day Free Trial", type: "trial" },
      ],
      chips: ["👀 View Demo Stores", "🚀 How to get website?", "💰 Pricing & Plans", "✨ Create Custom Website", "💸 Do you take commission?"],
    };
  }

  // 1B. WHAT IS OWNEZZY
  if (/(what is ownezzy|who are you|about ownezzy|what do you do|tell me about)/.test(q)) {
    return {
      text: "🛍️ **Ownezzy is an instant online store platform for Instagram & home business sellers!**\n\nInstead of managing messy DMs and manual GPay screenshots, Ownezzy gives you your own automated, branded website in 60 seconds with:\n• **14-Day Free Trial** (₹0, no credit card required)\n• **0% Sales Commission** (100% money goes to your bank)\n• **Direct UPI Payments** (PhonePe, GPay, Paytm, COD)\n• **Automated WhatsApp Order Alerts**",
      actions: [
        { label: "👀 View Demo Stores", type: "demos" },
        { label: "✨ Create Custom Website", type: "custom-modal" },
        { label: "🚀 Start 14-Day Free Trial", type: "trial" },
      ],
      chips: ["👀 View Demo Stores", "🚀 How to get website?", "💰 Pricing & Plans", "💸 Any commission?"],
    };
  }

  // 1C. DEMO STORES & WEBSITE CATALOG
  if (/(view demo|demo store|demo website|show demo|see demo|sample store|example store|all websites|show websites|list of websites|catalog|browse stores|browse websites|explore websites)/.test(q)) {
    return {
      text: "👀 **Explore 100+ Ready-to-Launch Demo Stores!**\n\nWe have beautifully crafted live demo stores for every category — Clothing, Phone Cases, Jewelry, Footwear, Plants, Home Decor, Organic Soaps, and more.\n\nClick below to browse our full live catalog:",
      actions: [
        { label: "👀 View Demo Stores", type: "demos" },
        { label: "✨ Create Custom Website", type: "custom-modal" },
        { label: "🚀 Start 14-Day Free Trial", type: "trial" },
      ],
      chips: ["🚀 How to get website?", "💰 Pricing & Plans", "✨ Create Custom Website", "💸 Any commission?"],
    };
  }

  // 2. PRICING & 14-DAY FREE TRIAL
  if (/(price|pricing|cost|plan|free trial|how much|charges|fee|subscription|expensive|rate|money)/.test(q)) {
    return {
      text: "🎉 You can start with our **14-Day 100% Free Trial** right now! No credit card is required.\n\n• **14-Day Free Trial:** ₹0 (Zero Risk, full feature access)\n• **Monthly Plan:** ₹399/month\n• **Yearly Plan:** ₹4,499/year (~₹375/month)\n\nAll plans include unlimited products, free subdomain, automated WhatsApp alerts, direct UPI payments, and **0% sales commission**!",
      actions: [
        { label: "🚀 Start 14-Day Free Trial", type: "trial" },
        { label: "👀 View Demo Stores", type: "demos" },
        { label: "💬 Chat on WhatsApp", type: "whatsapp", query: inputQuery },
      ],
      chips: ["👀 View Demo Stores", "🚀 How to get website?", "💸 Any commission?", "📱 How do UPI payments work?"],
    };
  }

  // 2B. MULTI-STORE & EXISTING USERS
  if (/(multiple|second website|another website|more than one|two websites|two stores|second store|already have|already bought|existing account|existing user|additional website|another store)/.test(q)) {
    return {
      text: "🏪 **Yes! You can manage multiple websites under a single account!**\n\n• **First Website:** Enjoys the 14-day ₹0 free trial!\n• **Additional Websites:** As free trial is 1 per account, simply choose a paid plan (Monthly ₹399 or Yearly ₹4,499) during checkout.\n• **Single Login:** All your stores share your same User ID and existing password — no need to remember multiple passwords!",
      actions: [
        { label: "✨ Create Custom Website", type: "custom-modal" },
        { label: "👀 View Demo Stores", type: "demos" },
        { label: "💬 Chat on WhatsApp", type: "whatsapp", query: inputQuery },
      ],
      chips: ["💰 Pricing & Plans", "👀 View Demo Stores", "🚀 How to get website?"],
    };
  }

  // 3. ZERO COMMISSION
  if (/(commission|cut|margin|percentage|per sale|hidden charge|profit)/.test(q)) {
    return {
      text: "✨ **Ownezzy charges 0% sales commission!**\n\nUnlike Amazon, Flipkart, or Meesho (who take 15% to 30% cuts on every sale), 100% of your earnings go straight into your own bank account.",
      actions: [
        { label: "👀 View Demo Stores", type: "demos" },
        { label: "🚀 Start 14-Day Free Trial", type: "trial" },
      ],
      chips: ["👀 View Demo Stores", "🚀 How to get website?", "💰 Pricing & Plans", "📱 How do UPI payments work?"],
    };
  }

  // 4. PAYMENTS & UPI (GPay, PhonePe, Paytm, COD)
  if (/(payment|upi|gpay|google pay|phonepe|paytm|bank|cod|cash on delivery|payout|gateway|money transfer)/.test(q)) {
    return {
      text: "💸 **Direct UPI & Bank Settlements:**\n\n• Customers can pay you using Google Pay, PhonePe, Paytm, BHIM UPI, Cards, or Netbanking.\n• You can also enable **Cash on Delivery (COD)**.\n• Every payment goes directly to your bank account with zero middleman delays!",
      actions: [
        { label: "🚀 Start Free Trial", type: "trial" },
        { label: "👀 View Demo Stores", type: "demos" },
        { label: "💬 Talk on WhatsApp", type: "whatsapp", query: inputQuery },
      ],
      chips: ["👀 View Demo Stores", "🚀 How to get website?", "💸 Any commission?", "🌐 Can I use my own domain?"],
    };
  }

  // 5. HOW TO GET WEBSITE / HOW IT WORKS / SETUP IN 60 SECONDS
  if (/(get website|how to get|want website|need website|buy website|create website|make website|open website|build website|start website|open store|launch store|start store|create store|how it works|how to start|setup|signup|sign up|register)/.test(q)) {
    return {
      text: "⚡ **Getting your own Ownezzy website takes just 60 seconds:**\n\n1. **Browse our 100+ demo stores** or tap 'Create Custom Website'\n2. **Enter your WhatsApp number & Email** (verify with a quick 6-digit OTP code)\n3. **Pick your store name** (e.g. `yourname.ownezzy.com`)\n\n✨ Your store is instantly live with a ready layout and UPI checkout. Just add your own products and start taking orders!",
      actions: [
        { label: "👀 View Demo Stores", type: "demos" },
        { label: "✨ Create Custom Website", type: "custom-modal" },
        { label: "🚀 Start 14-Day Free Trial", type: "trial" },
        { label: `💬 WhatsApp Support (${WHATSAPP_NUMBER})`, type: "whatsapp", query: inputQuery },
      ],
      chips: ["👀 View Demo Stores", "💰 Pricing & Free Trial", "✨ Create Custom Website", "💻 Do I need coding?"],
    };
  }

  // 5B. CUSTOM WEBSITE / UNLISTED BUSINESS
  if (/(custom website|custom store|customized|not in the list|not in list|not listed|other business|different business|unique business)/.test(q)) {
    return {
      text: "✨ **You can create a Customized Website for ANY business in 60 seconds!**\n\nChoose from 100+ categories or type your own custom niche. We provide:\n• Standard clean, modern store design\n• 14-Day Free Trial (₹0, no card needed)\n• Direct UPI payments & 0% sales commission\n• Instant WhatsApp order alerts",
      actions: [
        { label: "✨ Create Custom Website Now", type: "custom-modal" },
        { label: "👀 View Demo Stores", type: "demos" },
      ],
      chips: ["👀 View Demo Stores", "🚀 How to get website?", "💰 Pricing & Free Trial", "💸 Any commission?"],
    };
  }

  // 6. CUSTOM DOMAIN VS SUBDOMAIN
  if (/(domain|subdomain|godaddy|name|\.com|\.in|website address|url|custom domain)/.test(q)) {
    return {
      text: "🌐 **Flexible Store Addresses:**\n\n• Every store gets a free **`yourname.ownezzy.com`** subdomain for life!\n• Want your own brand domain like `kavyaboutique.com` or `trendcases.in`? You can connect your existing GoDaddy or Namecheap domain with 1 click from your admin panel.\n• Or buy a new domain with the **Monthly or Yearly Plan**: its yearly fee is shown at checkout.",
      actions: [
        { label: "🚀 Start Free Trial", type: "trial" },
        { label: "👀 View Demo Stores", type: "demos" },
        { label: "💬 Ask on WhatsApp", type: "whatsapp" },
      ],
      chips: ["👀 View Demo Stores", "💰 Pricing", "🚀 How to start", "💸 Any commission?"],
    };
  }

  // 7. NO CODING / TECHNICAL SKILLS NEEDED
  if (/(coding|code|technical|developer|computer|laptop|difficult|hard|mobile phone|skill)/.test(q)) {
    return {
      text: "📱 **Zero Coding or Technical Skills Needed!**\n\nYou can manage your entire store from your mobile phone. Adding a new product takes just 30 seconds: snap a photo, set your price, and tap save!\n\nPlus, our support team can even help you upload your first products for free.",
      actions: [
        { label: "🚀 Try It For Free", type: "trial" },
        { label: "👀 View Demo Stores", type: "demos" },
        { label: "💬 WhatsApp Support", type: "whatsapp" },
      ],
      chips: ["👀 View Demo Stores", "🚀 How it works", "💰 Pricing", "🎨 Available Niches"],
    };
  }

  // 8. AVAILABLE NICHES & STORE TEMPLATES
  if (/(template|niche|blouse|aari|cases|phone case|footwear|shoes|bags|craft|decor|soap|plant|saree|kurti|jewelry|clothes)/.test(q)) {
    return {
      text: "🎨 **100+ Specialized Live Store Niches Ready:**\n\n• Designer Blouses & Sarees (`blouses.ownezzy.com`)\n• Phone Cases & Accessories (`cases.ownezzy.com`)\n• Handmade Footwear & Juttis (`footwear.ownezzy.com`)\n• Handbags & Totes (`bags.ownezzy.com`)\n• Macrame & Handicrafts (`crafts.ownezzy.com`)\n• Home Decor (`decor.ownezzy.com`)\n• Soaps & Skincare (`soaps.ownezzy.com`)\n• Indoor Plants (`plants.ownezzy.com`)\n\nEvery demo shows 30 real products, and your own store starts with a clean layout ready for your products!",
      actions: [
        { label: "👀 View Demo Stores", type: "demos" },
        { label: "✨ Create Custom Website", type: "custom-modal" },
        { label: "🚀 Start 14-Day Free Trial", type: "trial" },
      ],
      chips: ["👀 View Demo Stores", "💰 Pricing", "📱 How do UPI payments work?", "🚀 Launch in 60s"],
    };
  }

  // 9. WHATSAPP ORDER NOTIFICATIONS
  if (/(whatsapp alert|notification|message|sms|order tracking|order alert)/.test(q)) {
    return {
      text: "📲 **Instant WhatsApp Notifications:**\n\nEvery time a customer places an order, you and your customer get an immediate WhatsApp message with:\n• Ordered items & customizations\n• Delivery address & phone number\n• Payment status & tracking receipt",
      actions: [
        { label: "🚀 Start Free Trial", type: "trial" },
        { label: "👀 View Demo Stores", type: "demos" },
        { label: "💬 Chat on WhatsApp", type: "whatsapp" },
      ],
      chips: ["👀 View Demo Stores", "💸 Any commission?", "💰 Pricing", "🚀 How it works"],
    };
  }

  // 10. HUMAN SUPPORT / TALK TO PERSON / CONTACT
  if (/(human|contact|support|help|whatsapp number|email|phone|talk|call|agent|person|representative|reach)/.test(q)) {
    return {
      text: "Please reach out to our customer support on:",
      actions: [
        { label: `💬 WhatsApp (+91 ${WHATSAPP_NUMBER})`, type: "whatsapp", query: inputQuery },
        { label: `✉️ Email (${SUPPORT_EMAIL})`, type: "email", query: inputQuery },
        { label: "👀 View Demo Stores", type: "demos" },
      ],
      chips: ["👀 View Demo Stores", "🚀 How to get website?", "💰 Pricing & Free Trial", "✨ Create Custom Website"],
    };
  }

  // 11. FALLBACK / UNRECOGNIZED QUERY
  return {
    text: "Please reach out to our customer support on:",
    actions: [
      { label: `💬 WhatsApp (+91 ${WHATSAPP_NUMBER})`, type: "whatsapp", query: inputQuery },
      { label: `✉️ Email (${SUPPORT_EMAIL})`, type: "email", query: inputQuery },
      { label: "👀 View Demo Stores", type: "demos" },
    ],
    chips: [
      "👀 View Demo Stores",
      "🚀 How to get website?",
      "💰 Pricing & Free Trial",
      "✨ Create Custom Website",
      "💸 Do you take commission?",
    ],
  };
}

export default function AiDogChatBot() {
  const drag = useDraggable('oz_aidog_position');
  const [isOpen, setIsOpen] = useState(false);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [messages, setMessages] = useState(() => {
    try {
      const saved = sessionStorage.getItem('oz_ai_chat_msgs');
      if (saved) return JSON.parse(saved);
    } catch {}
    return [
      {
        id: 1,
        sender: 'bot',
        text: "Woof! Hello! 👋 I'm your Ownezzy AI assistant.\n\nAsk me anything about launching your online store, the 14-day free trial, UPI payments, 0% commission, or custom domains!",
        time: 'Just now',
      },
    ];
  });

  const [activeChips, setActiveChips] = useState([
    "👀 View Demo Stores",
    "🚀 How to get website?",
    "💰 Pricing & Plans",
    "✨ Create Custom Website",
    "💸 Do you take commission?",
  ]);

  const messagesEndRef = useRef(null);

  useEffect(() => {
    try {
      sessionStorage.setItem('oz_ai_chat_msgs', JSON.stringify(messages));
    } catch {}
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isTyping]);

  const handleSend = (textToSend) => {
    const query = String(textToSend || input).trim();
    if (!query) return;

    const userMsg = {
      id: Date.now(),
      sender: 'user',
      text: query,
      time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
    };

    setMessages((prev) => [...prev, userMsg]);
    setInput('');
    setIsTyping(true);

    // Realistic typing delay (400-600ms)
    setTimeout(() => {
      try {
        const result = processAiQuery(query);
        const botMsg = {
          id: Date.now() + 1,
          sender: 'bot',
          text: result.text,
          actions: result.actions || [],
          time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        };

        setMessages((prev) => [...prev, botMsg]);
        if (result.chips && result.chips.length > 0) {
          setActiveChips(result.chips);
        }
      } catch (err) {
        console.error("AI Dog Chat error:", err);
        setMessages((prev) => [
          ...prev,
          {
            id: Date.now() + 1,
            sender: 'bot',
            text: "Please reach out to our customer support on:",
            actions: [
              { label: `💬 WhatsApp (+91 ${WHATSAPP_NUMBER})`, type: "whatsapp", query },
              { label: `✉️ Email (${SUPPORT_EMAIL})`, type: "email", query },
              { label: "👀 View Demo Stores", type: "demos" },
            ],
            time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
          },
        ]);
        setActiveChips([
          "👀 View Demo Stores",
          "🚀 How to get website?",
          "💰 Pricing & Free Trial",
          "✨ Create Custom Website",
          "💸 Do you take commission?",
        ]);
      } finally {
        setIsTyping(false);
      }
    }, 450);
  };

  const handleActionClick = (action) => {
    if (action.type === 'whatsapp') {
      const text = encodeURIComponent(`Hi Ownezzy Team! I have a question about Ownezzy: ${action.query || ''}`);
      window.open(`https://wa.me/${WHATSAPP_INTL}?text=${text}`, '_blank');
    } else if (action.type === 'email') {
      const subject = encodeURIComponent('Ownezzy Inquiry & Support');
      const body = encodeURIComponent(`Hi Ownezzy Team,\n\nI have a question: ${action.query || ''}\n\nThanks!`);
      window.open(`mailto:${SUPPORT_EMAIL}?subject=${subject}&body=${body}`, '_blank');
    } else if (action.type === 'trial') {
      // Trigger the offer modal if present on demo store
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new Event('oz:open-website-offer'));
      }
      // If on marketing site, scroll to plans or templates
      const plansEl = document.getElementById('plans') || document.getElementById('select-website');
      if (plansEl) {
        plansEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
        setIsOpen(false);
      }
    } else if (action.type === 'demos' || action.type === 'websites') {
      if (typeof window !== 'undefined') {
        const isOwnezzyMain = window.location.hostname === 'ownezzy.com' ||
          window.location.hostname === 'www.ownezzy.com' ||
          window.location.hostname === 'localhost';

        if (window.location.pathname === '/websites') {
          const grid = document.querySelector('.websites-catalog-grid') || document.querySelector('.websites-hero');
          if (grid) {
            grid.scrollIntoView({ behavior: 'smooth' });
          } else {
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }
        } else if (isOwnezzyMain) {
          window.location.href = '/websites';
        } else {
          window.location.href = 'https://www.ownezzy.com/websites';
        }
      }
      setIsOpen(false);
    } else if (action.type === 'custom-modal') {
      openCustomWebsiteModal();
      setIsOpen(false);
    }
  };

  const resetChat = () => {
    const initial = [
      {
        id: Date.now(),
        sender: 'bot',
        text: "Woof! Chat reset! 👋 How can I help you today?\n\nAsk me about our 14-day free trial, 0% commission, or how to launch your store in 60 seconds!",
        time: 'Just now',
      },
    ];
    setMessages(initial);
    setActiveChips([
      "👀 View Demo Stores",
      "🚀 How to get website?",
      "💰 Pricing & Plans",
      "✨ Create Custom Website",
      "💸 Do you take commission?",
    ]);
    try {
      sessionStorage.setItem('oz_ai_chat_msgs', JSON.stringify(initial));
    } catch {}
  };

  return (
    <>
      {/* Floating Dog Mascot Trigger (Movable / Draggable across screen) */}
      <button
        ref={drag.ref}
        type="button"
        className={`ownezzy-dog-trigger ${drag.isMoved ? 'is-moved' : ''} ${drag.isDragging ? 'is-dragging' : ''}`}
        style={drag.style}
        {...drag.handlers}
        onClick={() => setIsOpen((prev) => !prev)}
        aria-label="Ask Ownezzy AI Assistant"
        title="Click to chat with Ownezzy AI Buddy! (Drag to move)"
      >
        <div className="dog-ask-bubble">
          <span className="ask-sparkle">✨</span>
          <span>Ask ME</span>
        </div>
        <div className="dog-avatar-badge">
          <DogMascotSvg />
        </div>
      </button>

      {/* Expandable Chat Window */}
      {isOpen && (
        <aside className="ownezzy-chat-window" aria-label="Ownezzy AI Chat Assistant">
          {/* Header */}
          <header className="chat-window-header">
            <div className="chat-header-left">
              <div className="chat-header-avatar">
                <DogMascotSvg />
              </div>
              <div className="chat-header-title">
                <strong>Ownezzy Buddy 🐾</strong>
                <span className="chat-header-status">
                  <i className="status-dot" /> Online • 24/7 AI Helper
                </span>
              </div>
            </div>
            <div className="chat-header-actions">
              <button
                type="button"
                className="chat-header-btn"
                onClick={resetChat}
                title="Restart conversation"
                aria-label="Restart conversation"
              >
                ↺
              </button>
              <button
                type="button"
                className="chat-header-btn"
                onClick={() => setIsOpen(false)}
                title="Close chat"
                aria-label="Close chat"
              >
                ✕
              </button>
            </div>
          </header>

          {/* Messages Stream */}
          <div className="chat-messages-container">
            {messages.map((m) => (
              <div key={m.id} className={`chat-msg-row ${m.sender}`}>
                {m.sender === 'bot' && (
                  <div className="chat-bot-avatar">
                    <DogMascotSvg />
                  </div>
                )}
                <div className="chat-msg-bubble">
                  <div className="chat-msg-text">{m.text}</div>
                  {m.actions && m.actions.length > 0 && (
                    <div className="chat-action-pills">
                      {m.actions.map((act, idx) => (
                        <button
                          key={idx}
                          type="button"
                          className={`chat-action-btn ${act.type}-action`}
                          onClick={() => handleActionClick(act)}
                        >
                          {act.label}
                        </button>
                      ))}
                    </div>
                  )}
                  <span className="chat-msg-time">{m.time}</span>
                </div>
              </div>
            ))}

            {isTyping && (
              <div className="chat-msg-row bot">
                <div className="chat-bot-avatar">
                  <DogMascotSvg />
                </div>
                <div className="chat-msg-bubble">
                  <div className="typing-dots">
                    <span />
                    <span />
                    <span />
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Quick Suggestion Chips (Vertical List) */}
          {activeChips && activeChips.length > 0 && (
            <div className="chat-suggestion-chips" role="group" aria-label="Suggested questions">
              <div className="suggestion-chips-header">
                <span>Suggested questions:</span>
              </div>
              <div className="suggestion-chips-list">
                {activeChips.map((chip, idx) => (
                  <button
                    key={idx}
                    type="button"
                    className="chip-btn"
                    onClick={() => handleSend(chip.replace(/^[^\w\s]+\s*/, ''))}
                  >
                    <span className="chip-text">{chip}</span>
                    <span className="chip-arrow" aria-hidden="true">›</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Chat Input Bar */}
          <form
            className="chat-input-bar"
            onSubmit={(e) => {
              e.preventDefault();
              handleSend();
            }}
          >
            <input
              type="text"
              className="chat-input-field"
              placeholder="Ask anything about Ownezzy..."
              value={input}
              onChange={(e) => setInput(e.target.value)}
              aria-label="Type your message"
            />
            <button
              type="submit"
              className="chat-send-btn"
              disabled={!input.trim()}
              aria-label="Send message"
            >
              ➤
            </button>
          </form>

          {/* Footer Quick Support Contacts */}
          <footer className="chat-quick-contacts-bar">
            <span>Human Support:</span>
            <a href={`https://wa.me/${WHATSAPP_INTL}`} target="_blank" rel="noreferrer">
              💬 WhatsApp: {WHATSAPP_NUMBER}
            </a>
            <a href={`mailto:${SUPPORT_EMAIL}`}>
              ✉️ {SUPPORT_EMAIL}
            </a>
          </footer>
        </aside>
      )}
    </>
  );
}
